import { ref, onMounted, onUnmounted } from 'vue'
import {
  getFontSizePreference,
  setFontSizePreference,
  applyFontSizeToDOM,
} from '@/utils/preferences'
import { storageKey } from '@/services/types'
import { useAccountSettingsStore } from '@/stores/account-settings'
import type { FontSizePreference } from '@/types/user-settings'

const currentFontSize = ref<FontSizePreference>(getFontSizePreference())

// 修复项：使用 BroadcastChannel 实现跨标签页字体大小同步。
const fontSizeChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('ledger_ai_font_size')
  : null

export function useFontSize() {
  function apply() {
    applyFontSizeToDOM(currentFontSize.value)
  }

  function setFontSize(size: FontSizePreference) {
    currentFontSize.value = size
    setFontSizePreference(size)
    apply()
    // 修复项：通知其他标签页字体大小已变更。
    fontSizeChannel?.postMessage({ fontSize: size })

    // 修复项：异步同步到后端用户资料，实现多设备偏好一致性。
    setTimeout(() => {
      const store = useAccountSettingsStore()
      store.savePreferences({ fontSize: size })
    }, 0)
  }

  // 修复项：监听其他标签页通过 BroadcastChannel 发送的字体大小变更。
  function handleBroadcastMessage(event: MessageEvent) {
    if (event.data?.fontSize) {
      const newSize = event.data.fontSize as FontSizePreference
      currentFontSize.value = newSize
      setFontSizePreference(newSize)
      apply()
    }
  }

  // 修复项：降级方案 - 监听 storage 事件。
  function handleStorageChange(event: StorageEvent) {
    if (event.key === storageKey('font_size') && event.newValue) {
      const newSize = event.newValue as FontSizePreference
      currentFontSize.value = newSize
      apply()
    }
  }

  onMounted(() => {
    // 修复项：移除 onMounted 中的重复 apply()。
    // main.ts 已在应用 mount 前调用 initFontSize() 完成 DOM 初始化。
    // 字体大小无需系统级监听，这里仅注册跨标签页同步事件。
    fontSizeChannel?.addEventListener('message', handleBroadcastMessage)
    window.addEventListener('storage', handleStorageChange)
  })

  onUnmounted(() => {
    fontSizeChannel?.removeEventListener('message', handleBroadcastMessage)
    window.removeEventListener('storage', handleStorageChange)
  })

  return {
    currentFontSize,
    setFontSize,
    apply,
  }
}

// For non-setup usage (e.g. main.ts)
export function initFontSize() {
  const size = getFontSizePreference()
  applyFontSizeToDOM(size)
  currentFontSize.value = size
}
